import { Fragment } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Area, AreaChart, ReferenceLine,
} from 'recharts';
import { aedStr, ilsStr, eurStr, usdStr, pct, round, feeCaption, withResaleCosts, RESALE_DEFAULTS, areaBreakdown, perSqmStr } from '../lib/analysisEngine';
import { CHART_COLORS as C, compact } from '../lib/uiFormat';
import { useI18n } from '../lib/i18n';
import { StrategyBadge } from './StrAnalysis';
import ExecutiveSummary from './ExecutiveSummary';
import { CashFlowMobile } from './CashFlow';
import ReportFooter from './ReportFooter';

// Phone-optimised, single-column rendering of the same analysis snapshot used by
// the desktop <AnalysisReport>. Every figure comes from the same `analysis`
// object and the same formatters, so numbers match the A4 export exactly. This
// node is rendered off-screen at a fixed narrow width and rasterised to a tall
// 420pt-wide PDF (see lib/pdfExport.js). No animation / interactivity — static so
// the capture is deterministic.

const ACCENT = {
  green: 'text-green-600', blue: 'text-blue-600', amber: 'text-amber-600',
  gray: 'text-gray-900', red: 'text-red-600', purple: 'text-purple-600',
};

// Full-width stat card: label on top, big bold number below, optional ₪ line + caption.
function Stat({ label, value, sub, accent = 'gray', ilsText, eurText, usdText }) {
  return (
    <div className="rounded-[1.75rem] border border-black/[0.06] bg-white px-4 py-3">
      <p className="text-[12px] uppercase tracking-wide text-gray-400 font-medium leading-tight">{label}</p>
      <p className={`text-[30px] font-bold tabular-nums leading-tight mt-0.5 ${ACCENT[accent] || ACCENT.gray}`}>{value}</p>
      {ilsText && <p className="text-[12px] text-gray-400 tabular-nums">{ilsText}</p>}
      {eurText && <p className="text-[12px] text-gray-400 tabular-nums">{eurText}</p>}
      {usdText && <p className="text-[12px] text-gray-400 tabular-nums">{usdText}</p>}
      {sub && <p className="text-[12px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// Full-width ROI headline block — number-first, small uppercase label beneath, slim
// accent bar on top. Mirrors the desktop HeadlineStat card (one unified system).
function Headline({ label, value, sub, accent = 'green' }) {
  const bar = { green: 'bg-primary', gold: 'bg-gold', navy: 'bg-[#1E3A5F]' }[accent] || 'bg-primary';
  const text = { green: 'text-primary', gold: 'text-gold', navy: 'text-[#1E3A5F]' }[accent] || 'text-primary';
  return (
    <div className="relative overflow-hidden rounded-[1.75rem] border border-black/[0.06] bg-white p-4">
      <span className={`absolute top-0 left-0 right-0 h-1 ${bar}`} aria-hidden="true" />
      <p className={`text-[28px] font-bold tabular-nums leading-tight ${text}`}>{value}</p>
      <p className="text-[12px] uppercase tracking-wide text-gray-400 font-semibold mt-1">{label}</p>
      {sub && <p className="text-[12px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function SummaryCard({ label, aed, ils, eur, usd, accent = 'gray', caption }) {
  const c = { green: 'text-green-600', blue: 'text-blue-600', amber: 'text-amber-600', gray: 'text-gray-800' }[accent];
  return (
    <div className="rounded-xl border border-black/[0.06] p-3 bg-gray-50">
      <p className="text-[12px] text-gray-500 mb-0.5">{label}</p>
      <p className={`text-[18px] font-bold tabular-nums ${c}`}>{aedStr(aed)}</p>
      <p className="text-[12px] text-gray-400">{ilsStr((Number(aed) || 0) * ils)}</p>
      {eur != null && <p className="text-[12px] text-gray-400">{eurStr((Number(aed) || 0) * eur)}</p>}
      {usd != null && <p className="text-[12px] text-gray-400">{usdStr((Number(aed) || 0) * usd)}</p>}
      {caption && <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">{caption}</p>}
    </div>
  );
}

function ChartBlock({ title, subtitle, children }) {
  return (
    <div className="rounded-[1.75rem] bg-white border border-black/[0.06] p-4">
      <h3 className="font-semibold text-gray-800 text-[15px]">{title}</h3>
      {subtitle && <p className="text-[12px] text-gray-400 mt-0.5 mb-2">{subtitle}</p>}
      <div className={subtitle ? '' : 'mt-2'}>{children}</div>
    </div>
  );
}

function AedIlsTooltip({ active, payload, label, ils, eur, usd }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white/95 border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-[12px]">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.filter(p => p.value != null).map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.stroke || p.fill }} />
          <span className="text-gray-500">{p.name}</span>
          <span className="ml-auto font-medium text-gray-800">{aedStr(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

const CHART_H = 300;
const mkHandoverLabel = (value) => ({ value, position: 'insideTopRight', fontSize: 11, fill: C.handover, fontWeight: 600 });

export default function MobileReport({ project, unit, analysis, ils, eur, usd, assumptions, strategy, resale = RESALE_DEFAULTS, fixedRate = null }) {
  const { t, lang, fmtMonthYear: fmtMY, tMilestone } = useI18n();
  if (!analysis || !unit) return null;
  const a = analysis;
  const strat = strategy || a.strategy || 'ltr';
  const isStr = strat === 'str';
  const today = new Date();
  const handoverLabel = mkHandoverLabel(t('handover'));

  const handoverYear = a.atHandover?.year;
  const hoIdx = a.schedule.findIndex(r => r.isHandover);
  const payHandoverX = hoIdx >= 0 && a.paymentChart[hoIdx]
    ? (a.hasDates ? a.paymentChart[hoIdx].dateLabel : a.paymentChart[hoIdx].name) : null;
  const gainsData = a.years.filter(y => y.i >= a.constructionYears);

  // Static exit scenario: n = 0 (flip at handover) — matches the desktop card's default.
  // Resale costs (broker + NOC) applied via the shared helper so the number equals
  // the Flip hero card and the desktop Exit Scenario exactly.
  const exit = withResaleCosts((a.exitScenarios && a.exitScenarios[0]) || null, resale);
  const exitHold = exit ? (exit.n === 0 ? t('at_handover_lc') : t('x_years_after_handover', { n: exit.n })) : '';

  return (
    <div style={{ width: 400 }} className="bg-gray-50 text-gray-900">
      {/* branded document header */}
      <div className="relative overflow-hidden text-white bg-gradient-to-r from-dark via-dark to-card px-4 py-4">
        <p className="text-[11px] uppercase tracking-[0.2em] text-primary/80 font-semibold">{t('investment_analysis')}</p>
        <p className="text-[15px] font-bold leading-tight mt-0.5">{project.project_name} · {unit.unit_type}{unit.unit_number ? ` · ${t('unit_kw', { n: unit.unit_number })}` : ''}</p>
        <p className="text-[12px] text-gray-300">Skyhigh Dubai · David Barilier</p>
      </div>

      <div className="p-4 space-y-4">
        {/* ===== HERO — project photo (or brand gradient) + gradient overlay ===== */}
        <section className="relative overflow-hidden rounded-[1.75rem] text-white">
          <div className="absolute inset-0 bg-cover bg-center"
            style={project.hero_image_url
              ? { backgroundImage: `url(${project.hero_image_url})` }
              : { background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 55%, #17513f 130%)' }} />
          <div className="absolute inset-0" style={{ background: project.hero_image_url
            ? 'linear-gradient(180deg, rgba(10,14,24,0.15) 0%, rgba(10,14,24,0.55) 60%, rgba(10,14,24,0.92) 100%)'
            : 'linear-gradient(180deg, rgba(10,14,24,0.05) 0%, rgba(10,14,24,0.30) 100%)' }} />
          <div className="relative z-10 p-4 pt-6">
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              <span className="text-[9px] uppercase tracking-widest font-semibold bg-white/10 border border-white/20 rounded-full px-2 py-0.5">{t('off_plan')}</span>
              {project.is_phpp && <span className="text-[9px] uppercase tracking-widest font-semibold bg-white/10 border border-white/20 rounded-full px-2 py-0.5">PHPP</span>}
              <StrategyBadge strategy={strat} />
              {a.hasMarket && a.discountPct > 0 && (
                <span className="text-[9px] uppercase tracking-widest font-bold bg-primary rounded-full px-2 py-0.5">{t('below_market', { x: pct(a.discountPct) })}</span>
              )}
            </div>
            <h1 className="text-[24px] font-bold leading-tight tracking-tight">{project.project_name}</h1>
            <p className="text-[13px] text-white/70 mt-1">
              {[project.developer_name, project.area, unit.unit_type, unit.unit_number ? t('unit_kw', { n: unit.unit_number }) : null].filter(Boolean).join(' · ')}
            </p>
            {a.internal > 0 && <p className="text-[11px] text-white/45 mt-0.5">{areaBreakdown(a.internal, a.balcony, lang)}</p>}
            <div className="mt-3 pt-3 border-t border-white/15 flex items-end justify-between gap-3">
              {project.handover_date && (
                <div className="inline-flex items-center gap-1 bg-white/10 border border-white/15 rounded-full pl-2.5 pr-3 py-1 text-[11px] text-white/60">
                  <span>{t('handover')}</span>
                  <span className="font-semibold text-white tabular-nums">{fmtMY(project.handover_date)}</span>
                </div>
              )}
              <div className={!project.handover_date ? 'ml-auto' : ''}>
                <p className="text-[30px] font-bold tabular-nums leading-none">{aedStr(unit.price_aed)}</p>
                <p className="text-[13px] text-white/55 tabular-nums mt-1">{ilsStr(unit.price_aed * ils)}</p>
                {eur != null && <p className="text-[13px] text-white/55 tabular-nums">{eurStr(unit.price_aed * eur)}</p>}
                {usd != null && <p className="text-[13px] text-white/55 tabular-nums">{usdStr(unit.price_aed * usd)}</p>}
              </div>
            </div>
          </div>
        </section>

        {/* ===== EXECUTIVE SUMMARY (auto-generated) ===== */}
        <ExecutiveSummary analysis={a} ils={ils} eur={eur} usd={usd} className="!mb-0" />

        {/* ===== KEY NUMBERS (stacked) ===== */}
        <div className="space-y-3">
          <Stat label={t('until_handover')} value={aedStr(a.bucketUntil)} ilsText={ilsStr(a.bucketUntil * ils)} eurText={eur != null ? eurStr(a.bucketUntil * eur) : null} usdText={usd != null ? usdStr(a.bucketUntil * usd) : null}
            sub={feeCaption(a.feeParts?.until, lang) || t('paid_during_construction')} accent="amber" />
          <Stat label={t('on_handover')} value={aedStr(a.bucketOnHandover)} ilsText={ilsStr(a.bucketOnHandover * ils)} eurText={eur != null ? eurStr(a.bucketOnHandover * eur) : null} usdText={usd != null ? usdStr(a.bucketOnHandover * usd) : null}
            sub={feeCaption(a.feeParts?.onHandover, lang) || (isStr ? t('incl_furnishing') : t('final_milestone'))} accent="amber" />
          {a.hasPhpp && (
            <Stat label={t('post_handover_phpp')} value={aedStr(a.bucketPost)} ilsText={ilsStr(a.bucketPost * ils)} eurText={eur != null ? eurStr(a.bucketPost * eur) : null} usdText={usd != null ? usdStr(a.bucketPost * usd) : null}
              sub={t('phpp_installments_total')} accent="amber" />
          )}
          {isStr
            ? <Stat label={t('str_net_income_yr')} value={aedStr(a.str.net)} sub={t('gross_x', { x: aedStr(a.str.gross) })} accent={a.str.net >= 0 ? 'green' : 'red'} />
            : <Stat label={t('expected_rent_yr')} value={aedStr(a.startRent)} sub={t('gross_yield_x', { x: pct(a.grossYield) })} accent="green" />}
          <Stat label={isStr ? t('str_net_yield') : t('net_rental_yield')} value={pct(a.netYield)} sub={isStr ? t('incl_furnishing') : t('after_service_charge')} accent={a.netYield >= 0 ? 'green' : 'red'} />
          <Stat label={t('roi_at_maturity_y', { y: a.maturityYears })} value={pct(a.roiAt(a.atMaturity))} sub={t('capital_on_cash')} accent={a.roiAt(a.atMaturity) >= 0 ? 'green' : 'red'} />
        </div>

        {/* ===== INSTANT EQUITY ===== */}
        {a.hasMarket && (
          <div className="rounded-[1.75rem] p-4 text-white bg-gradient-to-br from-green-500 to-emerald-600">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className="font-semibold text-[13px]">{t('instant_equity_below_short')}</h3>
              <span className="text-[26px] font-extrabold leading-none tabular-nums">{pct(a.discountPct)}</span>
            </div>
            <div className="space-y-2 text-[13px]">
              <div className="flex justify-between"><span className="opacity-90">{t('your_price')}</span><span className="font-bold tabular-nums">{aedStr(a.price)}</span></div>
              <div className="flex justify-between"><span className="opacity-90">{t('market_value')}</span><span className="font-bold tabular-nums">{aedStr(a.marketValue)}</span></div>
              <div className="flex justify-between border-t border-white/25 pt-2"><span className="opacity-90">{t('instant_equity_day1')}</span><span className="font-extrabold tabular-nums">{aedStr(a.discountAmount)}</span></div>
            </div>
          </div>
        )}

        {/* ===== ROI HEADLINES (stacked full-width) ===== */}
        <div className="space-y-3">
          <Headline label={t('capital_roi_handover')} value={pct(a.roiAt(a.atHandover))} sub={t('keep_unit_ho')} accent="green" />
          <Headline label={t('flip_handover_roi')} value={pct(exit ? exit.roi : a.flipROI)} sub={t('resell_at_handover')} accent="gold" />
          <Headline label={t('capital_roi_plus_y', { y: a.maturityYears })} value={pct(a.roiAt(a.atMaturity))} sub={t('on_cash_deployed_x', { x: a.atMaturity ? a.atMaturity.year : t('maturity') })} accent="green" />
          <Headline label={t('net_rental_yield_card')} value={pct(a.netYield)} sub={t('gross_x_coc_y', { x: pct(a.grossYield), y: pct(a.cashYield) })} accent="navy" />
        </div>

        {/* ===== PRICE PER SQFT ===== */}
        <div className="rounded-[1.75rem] bg-white border border-black/[0.06] p-4">
          <h3 className="font-semibold text-gray-800 text-[14px] mb-1">{t('price_per_sqft')}</h3>
          <p className="text-[24px] font-bold text-gray-900 tabular-nums">{a.totalSqft > 0 ? aedStr(a.pricePerSqft) : '—'}<span className="text-[14px] font-normal text-gray-400">{t('per_sqft_suffix')}</span>{a.totalSqft > 0 && <span className="text-[14px] font-normal text-gray-400"> · {perSqmStr(a.pricePerSqft)}</span>}<span className="text-[12px] font-normal text-gray-300">{t('total_area')}</span></p>
          <p className="text-[12px] text-gray-400">
            {t('x_total_sqft', { x: round(a.totalSqft).toLocaleString('en-GB') })}
            {a.totalSqft > a.internal ? ` · ${t('x_internal_y_balcony', { i: round(a.internal).toLocaleString('en-GB'), b: round(a.balcony).toLocaleString('en-GB') })}` : ''}
            {a.hasMarket ? ` · ${t('market_x_sqft', { x: aedStr(a.marketPricePerSqft) })}` : ''}
          </p>
        </div>

        {/* ===== PAYMENT SCHEDULE — stacked milestone blocks ===== */}
        <div className="rounded-[1.75rem] bg-white border border-black/[0.06] p-4">
          <h3 className="font-semibold text-gray-800 text-[15px] mb-3">{t('payment_schedule')}{unit.unit_number && <span className="ml-1.5 font-normal text-[12px] text-gray-400">· {t('unit_kw', { n: unit.unit_number })}</span>}</h3>
          <div className="space-y-2">
            {a.schedule.map((r, i) => {
              const prev = a.schedule[i - 1];
              const showToday = !!(prev && prev.date < today && r.date >= today);
              return (
                <Fragment key={i}>
                  {showToday && (
                    <div className="flex items-center gap-2 py-1">
                      <div className="h-px flex-1 bg-rose-300" />
                      <span className="text-[10px] font-semibold text-rose-500 uppercase tracking-wide">{t('today')}</span>
                      <div className="h-px flex-1 bg-rose-300" />
                    </div>
                  )}
                  <div className={`rounded-xl border p-3 ${r.isFee ? 'bg-amber-50/60 border-amber-100' : 'bg-white border-black/[0.06]'}`}>
                    <div className="flex justify-between items-start gap-2">
                      <p className={`text-[14px] font-medium ${r.isFee ? 'text-amber-700' : 'text-gray-800'}`}>
                        {r.isFee ? '🧾 ' : ''}{tMilestone(r.label)}
                        {r.isHandover && <span className="ml-1.5 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full align-middle">{t('badge_handover')}</span>}
                        {r.isPost && <span className="ml-1.5 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full align-middle">PHPP</span>}
                      </p>
                      <span className="text-[13px] text-gray-500 tabular-nums shrink-0">{r.pctVal != null ? pct(r.pctVal) : t('fee')}</span>
                    </div>
                    <p className="text-[12px] text-gray-400 mt-0.5 tabular-nums">{fmtMY(r.date)}</p>
                    <div className="flex justify-between items-end mt-1.5">
                      <div>
                        <p className="text-[18px] font-bold text-gray-800 tabular-nums leading-tight">{aedStr(r.amount)}</p>
                        <p className="text-[12px] text-gray-400 tabular-nums">{ilsStr(r.amount * ils)}</p>
                        {eur != null && <p className="text-[12px] text-gray-400 tabular-nums">{eurStr(r.amount * eur)}</p>}
                        {usd != null && <p className="text-[12px] text-gray-400 tabular-nums">{usdStr(r.amount * usd)}</p>}
                      </div>
                      <div className="text-right shrink-0 w-[110px]">
                        <p className="text-[12px] text-gray-500 tabular-nums mb-1">{t('x_paid', { x: pct(r.cumPct) })}</p>
                        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, Math.max(0, r.cumPct))}%` }} />
                        </div>
                        <p className="text-[11px] text-gray-400 tabular-nums mt-1">{t('cum_x', { x: aedStr(r.cumAED) })}</p>
                        <p className="text-[10px] text-gray-400 tabular-nums">{ilsStr(r.cumAED * ils)}{eur != null && <> · {eurStr(r.cumAED * eur)}</>}{usd != null && <> · {usdStr(r.cumAED * usd)}</>}</p>
                      </div>
                    </div>
                  </div>
                </Fragment>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <SummaryCard label={t('until_handover')} aed={a.bucketUntil} ils={ils} eur={eur} usd={usd} accent="amber" caption={feeCaption(a.feeParts?.until, lang)} />
            <SummaryCard label={t('on_handover')} aed={a.bucketOnHandover} ils={ils} eur={eur} usd={usd} accent="amber" caption={feeCaption(a.feeParts?.onHandover, lang)} />
            {a.hasPhpp && <SummaryCard label={t('post_handover_phpp')} aed={a.bucketPost} ils={ils} eur={eur} usd={usd} accent="blue" />}
            {a.hasPhpp && a.atPhppEnd && (() => {
              const grossByThen = isStr ? a.atPhppEnd.cumStrGross : a.atPhppEnd.cumGrossRent;
              return (
                <SummaryCard label={t('net_by_phpp_end')} aed={a.netOutOfPocketAt(a.atPhppEnd)} ils={ils} eur={eur} usd={usd} accent="blue"
                  caption={t('net_by_phpp_end_caption', { rent: aedStr(grossByThen), year: a.phppEndYear })} />
              );
            })()}
            <SummaryCard label={t('grand_total_incl_fees')} aed={a.grandTotal} ils={ils} eur={eur} usd={usd} accent="gray" />
          </div>
        </div>

        {/* ===== RETURN ON INVESTMENT — stacked horizon blocks ===== */}
        <div className="rounded-[1.75rem] bg-white border border-black/[0.06] p-4">
          <h3 className="font-semibold text-gray-800 text-[15px] mb-3">{t('return_on_investment')}</h3>
          <div className="space-y-2">
            {[[t('at_handover'), a.atHandover], [t('plus_y_midpoint', { y: a.midYears }), a.atMid], [t('plus_y_maturity', { y: a.maturityYears }), a.atMaturity]].map(([lbl, row]) => row && (
              <div key={lbl} className="rounded-xl border border-black/[0.06] bg-gray-50 p-3">
                <p className="text-[14px] font-semibold text-gray-800 mb-1.5">{lbl}</p>
                <div className="grid grid-cols-2 gap-y-1.5 text-[13px]">
                  <span className="text-gray-500">{t('property_value')}</span>
                  <span className="text-right tabular-nums text-gray-800 font-medium">{aedStr(row.value)}</span>
                  <span className="text-gray-500">{t('cash_deployed')}</span>
                  <span className="text-right tabular-nums text-gray-600">{aedStr(a.cashDeployedAt(row))}</span>
                  <span className="text-gray-500">{t('capital_roi')}</span>
                  <span className="text-right tabular-nums text-green-600 font-medium">{pct(a.roiAt(row))}</span>
                  <span className="text-gray-500">{t('cum_net_rent')}</span>
                  <span className="text-right tabular-nums text-gray-700">{aedStr(row.cumNetRent)}</span>
                  <span className="text-gray-500">{t('combined_roi')}</span>
                  <span className="text-right tabular-nums text-blue-600 font-semibold">{pct(a.combinedAt(row))}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3 text-[13px]">
            {[[t('gross_yield'), pct(a.grossYield)], [t('net_yield'), pct(a.netYield)], [t('coc_yield'), pct(a.cashYield)], [t('combined_roi_ann'), pct(a.combinedMaturityAnnual)], [t('break_even'), a.breakEven ? t('yr_x', { x: a.breakEven.year }) : t('gt_x_yr', { x: a.maturityYears })]].map(([l, v]) => (
              <div key={l} className="bg-gray-50 rounded-xl p-2.5 text-center">
                <p className="text-[12px] text-gray-400 mb-0.5">{l}</p>
                <p className="font-bold text-gray-800 tabular-nums">{v}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ===== EXIT SCENARIO (static, full-width) ===== */}
        {exit && (
          <div className="rounded-[1.75rem] p-4 text-white bg-gradient-to-br from-dark to-card">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h3 className="font-semibold text-[15px]">{t('exit_scenario')}</h3>
              <span className="text-[11px] text-gray-400">{t('sell_x', { date: fmtMY(exit.exitDate) })}</span>
            </div>
            <p className="text-[12px] text-gray-400 mb-3">{t('exit_profit_roi_sell', { hold: exitHold })}</p>
            <div className="space-y-2 text-[13px]">
              <div className="flex justify-between"><span className="text-gray-400">{t('sale_value_at_exit', { market: '' })}</span><span className="font-medium tabular-nums">{aedStr(exit.saleValue)}</span></div>
              {exit.resale && exit.commission > 0 && <div className="flex justify-between gap-2"><span className="text-gray-400">{t('less_broker_commission_x', { x: pct(resale.commissionPct) })}</span><span className="font-medium tabular-nums text-red-300">−{aedStr(exit.commission)}</span></div>}
              {exit.resale && exit.noc > 0 && <div className="flex justify-between gap-2"><span className="text-gray-400">{t('less_noc_fee')}</span><span className="font-medium tabular-nums text-red-300">−{aedStr(exit.noc)}</span></div>}
              <div className="flex justify-between gap-2"><span className="text-gray-400">{t('less_purchase_price')}</span><span className="font-medium tabular-nums text-red-300">−{aedStr(exit.purchasePrice)}</span></div>
              {exit.netRent > 0 && <div className="flex justify-between gap-2"><span className="text-gray-400">{t('plus_net_rent_collected')}</span><span className="font-medium tabular-nums text-green-300">+{aedStr(exit.netRent)}</span></div>}
              <div className="flex justify-between border-t border-gray-700 pt-2">
                <span className="text-gray-300">{t('exit_profit')}</span>
                <span className="text-[20px] font-bold text-green-400 tabular-nums">{aedStr(exit.profit)}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="bg-white/10 rounded-xl p-3 text-center">
                <p className="text-[12px] text-gray-300">{t('roi_on_cash')}</p>
                <p className="text-[20px] font-bold text-amber-400 tabular-nums">{pct(exit.roi)}</p>
              </div>
              <div className="bg-white/10 rounded-xl p-3 text-center">
                <p className="text-[12px] text-gray-300">{t('annualized_roi')}</p>
                <p className="text-[20px] font-bold text-amber-400 tabular-nums">{pct(exit.annualizedRoi)}</p>
              </div>
            </div>
            <p className="text-[11px] text-gray-500 mt-3 leading-snug">
              {exit.resale ? t('resale_incl_note', { c: pct(resale.commissionPct), n: aedStr(resale.nocFee) }) : t('resale_excl_note')}
            </p>
          </div>
        )}

        {/* ===== CASH FLOW (yearly) ===== */}
        <CashFlowMobile a={a} ils={ils} eur={eur} usd={usd} />

        {/* ===== CHARTS — full-width, one per row, taller ===== */}
        <ChartBlock title={t('cumulative_payments')} subtitle={t('money_in_over_time')}>
          {a.paymentChart.length ? (
            <ResponsiveContainer width="100%" height={CHART_H}>
              <AreaChart data={a.paymentChart} margin={{ top: 8, right: 10, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="mPay" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.payment} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={C.payment} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey={a.hasDates ? 'dateLabel' : 'name'} tick={{ fontSize: 11 }} interval="preserveStartEnd" angle={-25} textAnchor="end" height={56} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={compact} width={46} />
                <Tooltip content={<AedIlsTooltip ils={ils} eur={eur} usd={usd} />} />
                {payHandoverX != null && <ReferenceLine x={payHandoverX} stroke={C.handover} strokeDasharray="4 3" label={handoverLabel} />}
                <Area type="stepAfter" dataKey="cumulative" name={t('cumulative_paid')} stroke={C.payment} strokeWidth={2} fill="url(#mPay)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : null}
        </ChartBlock>

        <ChartBlock title={t('property_value_vs_invested')}>
          {a.years.length ? (
            <ResponsiveContainer width="100%" height={CHART_H}>
              <LineChart data={a.years} margin={{ top: 8, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={compact} width={46} />
                <Tooltip content={<AedIlsTooltip ils={ils} eur={eur} usd={usd} />} />
                {handoverYear != null && <ReferenceLine x={handoverYear} stroke={C.handover} strokeDasharray="4 3" label={handoverLabel} />}
                <Line type="monotone" dataKey="value" name={t('property_value_series')} stroke={C.value} strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="invested" name={t('total_invested')} stroke={C.invested} strokeWidth={2} dot={false} strokeDasharray="5 4" isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : null}
          <div className="flex flex-wrap gap-3 mt-2 text-[12px] text-gray-500">
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ background: C.value }} /> {t('property_value_series')}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ background: C.invested }} /> {t('total_invested')}</span>
          </div>
        </ChartBlock>

        <ChartBlock title={t('annual_gains')}>
          {gainsData.length ? (
            <ResponsiveContainer width="100%" height={CHART_H}>
              <BarChart data={gainsData} margin={{ top: 8, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={compact} width={46} />
                <Tooltip content={<AedIlsTooltip ils={ils} eur={eur} usd={usd} />} />
                <Bar dataKey="netRent" name={t('net_rent')} stackId="a" fill={C.rent} isAnimationActive={false} />
                {a.hasMarket && <Bar dataKey="instantEquity" name={t('instant_equity_discount')} stackId="a" fill={C.equity} isAnimationActive={false} />}
                <Bar dataKey="marketAppr" name={a.hasMarket ? t('market_appreciation') : t('appreciation_cum')} stackId="a" fill={C.appreciation} radius={[4, 4, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          ) : null}
        </ChartBlock>

        <ChartBlock title={t('combined_roi_over_time')} subtitle={a.breakEven ? t('break_even_in_year_x', { x: a.breakEven.year }) : t('cum_appr_vs_cost')}>
          {a.years.length ? (
            <ResponsiveContainer width="100%" height={CHART_H}>
              <LineChart data={a.years} margin={{ top: 8, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={v => v.toFixed(0) + '%'} width={46} />
                {handoverYear != null && <ReferenceLine x={handoverYear} stroke={C.handover} strokeDasharray="4 3" label={handoverLabel} />}
                <ReferenceLine y={0} stroke="#e5e7eb" />
                <Line type="monotone" dataKey="combinedROI" name={t('combined_roi_series')} stroke={C.roi} strokeWidth={2.5} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : null}
        </ChartBlock>

        <ReportFooter fixedRate={fixedRate} />
      </div>
    </div>
  );
}
